import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useDictionary } from '@/lib/i18n'

import { STAGE_SIMULATION } from '@/constants/bike'

// Emphasis bar chart built as a real table: the leader is marked in race red, the rest stay neutral grey.
const StageChart: FC = (): ReactNode => {
    const t = useDictionary()
    const chart = t.timeToFinish.chart
    const maxGap = Math.max(...STAGE_SIMULATION.map((row) => row.gap))

    return (
        <figure className='rounded-3xl border border-white/10 bg-[oklch(0.13_0.004_286/0.88)] p-6 shadow-2xl backdrop-blur sm:p-8'>
            <figcaption>
                <p className='display-heading font-heading text-2xl font-semibold uppercase tracking-tight text-foreground sm:text-3xl'>
                    {chart.title}
                </p>
                <p className='mt-1 text-sm text-muted-foreground'>
                    {chart.subtitle}
                </p>
            </figcaption>

            <table className='mt-8 w-full border-collapse text-sm'>
                <thead className='sr-only'>
                    <tr>
                        <th scope='col'>{chart.columns.bike}</th>
                        <th scope='col'>{chart.columns.gap}</th>
                        <th scope='col'>{chart.columns.time}</th>
                    </tr>
                </thead>

                <tbody className='group/rows'>
                    {STAGE_SIMULATION.map((row) => {
                        const leader = row.gap === 0

                        return (
                            <tr key={row.id} className='transition-opacity duration-200 group-hover/rows:opacity-45 hover:opacity-100!'>
                                <th
                                    scope='row'
                                    className={cn(
                                        'w-[1%] whitespace-nowrap py-3 pr-4 text-left font-medium',
                                        leader ? 'text-foreground' : 'text-foreground/80'
                                    )}
                                >
                                    {row.name}
                                </th>

                                <td className='py-3'>
                                    <div className='flex h-6 items-center border-l border-white/25'>
                                        {leader ? (
                                            <span className='ml-2 inline-flex items-center gap-2'>
                                                <span aria-hidden='true' className='size-3 rounded-full bg-race ring-2 ring-[oklch(0.13_0.004_286)]' />
                                                <span className='text-xs font-semibold uppercase tracking-[0.14em] text-foreground'>
                                                    {chart.fastest}
                                                </span>
                                            </span>
                                        ) : (
                                            <>
                                                <span
                                                    aria-hidden='true'
                                                    className='h-5 rounded-r-[4px] bg-chart-2'
                                                    style={{ width: `${(row.gap / maxGap) * 78}%` }}
                                                />
                                                <span className='ml-2 whitespace-nowrap text-xs font-medium tabular-nums text-foreground'>
                                                    +{row.gap} {chart.unit}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </td>

                                <td className='w-[1%] whitespace-nowrap py-3 pl-4 text-right font-mono text-xs tabular-nums text-muted-foreground'>
                                    {row.time}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            <p className='mt-6 border-t border-white/10 pt-4 text-xs leading-relaxed text-muted-foreground'>
                {chart.note}
            </p>
        </figure>
    )
}

export default StageChart
